import React from 'react'

export type Countdown = {
  days: number
  hours: number
  minutes: number
  seconds: number
  /** Total milliseconds remaining (0 once the deadline has passed). */
  remainingMs: number
  /** True once the deadline is reached. */
  isElapsed: boolean
}

const SECOND = 1000
const MINUTE = 60 * SECOND
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

const breakdown = (remainingMs: number): Countdown => ({
  days: Math.floor(remainingMs / DAY),
  hours: Math.floor((remainingMs % DAY) / HOUR),
  minutes: Math.floor((remainingMs % HOUR) / MINUTE),
  seconds: Math.floor((remainingMs % MINUTE) / SECOND),
  remainingMs,
  isElapsed: remainingMs <= 0
})

/**
 * A live countdown to `deadline`.
 *
 * Ticks once per second while more than a day remains would be wasteful, but
 * the seconds figure is exactly what makes a deadline feel real, so we tick
 * every second and stop the moment the deadline passes. Passing `null` (no
 * deadline) parks the hook without scheduling anything.
 */
const useCountdown = (deadline: Date | null): Countdown => {
  const deadlineMs = deadline ? deadline.getTime() : null

  const compute = React.useCallback(
    () => breakdown(deadlineMs === null ? 0 : Math.max(0, deadlineMs - Date.now())),
    [deadlineMs]
  )

  const [countdown, setCountdown] = React.useState<Countdown>(compute)

  React.useEffect(() => {
    // Re-sync immediately: the deadline may have changed since the last render,
    // and we should not wait a full second to show the correct figure.
    setCountdown(compute())

    if (deadlineMs === null || deadlineMs <= Date.now()) return undefined

    const interval = window.setInterval(() => {
      const next = compute()
      setCountdown(next)
      if (next.isElapsed) window.clearInterval(interval)
    }, SECOND)

    return () => window.clearInterval(interval)
  }, [compute, deadlineMs])

  return countdown
}

export default useCountdown
